import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import classes from "./WidgetCardsLandscape.module.css";
import {
    _getWidgetAnimStagger,
    _entryAnimStarted,
    _entryAnimEnded,
    _whenNoEntryAnimations,
} from "./entryAnimCoordinator";

import LandscapeTicketCard, { EMPTY_CARD_STATE } from "./LandscapeTicketCard";

const areSameCards = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const p = a[i]; const n = b[i];
        if (
            p.id !== n.id || p.title !== n.title || p.description !== n.description ||
            p.owner !== n.owner || p.percent !== n.percent || p.topLeftText !== n.topLeftText ||
            p.imageUrl !== n.imageUrl || p.openDate !== n.openDate || p.promiseDate !== n.promiseDate ||
            p.status !== n.status || p.headerText !== n.headerText ||
            p.completedDate !== n.completedDate || p.targetHours !== n.targetHours ||
            p.actualHours !== n.actualHours || p.description1 !== n.description1
        ) return false;
    }
    return true;
};


export default function WidgetCardsLandscape({
    cardsData = [],
    minOpenTicketCount,
    autoRevertDurationinSec = 0,
    isDarkTheme = false,
    OpenTicketPlacement,
    widgetViewportWidth = 0,
    widgetViewportHeight = 0,
}) {
    const parentRef = useRef(null);
    const gridRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const cardRefs = useRef([]);
    const autoRevertTimerRef = useRef(null);
    const resizeDebounceRef = useRef(null);
    const lastCalcTimeRef = useRef(0);
    const initialStateRef = useRef(null);
    const isFirstCalculationRef = useRef(true);
    const calcRetryRef = useRef(0);
    const calcHandlerRef = useRef(null);
    const calcRunIdRef = useRef(0);
    const lastDprRef = useRef(window.devicePixelRatio || 1);
    const dataVersionRef = useRef(0);  // Track data version to prevent stale state restoration
    const isCalculatingRef = useRef(false);  // Track if calculation is in progress
    const hasUserInteractedRef = useRef(false);
    const toggleCardLatestRef = useRef(null);
    const onToggleCard = useCallback((i) => toggleCardLatestRef.current?.(i), []);
    const prevDisplayCardsRef = useRef([]);
    const prevLayoutDepsRef = useRef({ minOpenTicketCount: null, isOpenTicketPlacementTop: null });
    const isEntryAnimatingRef = useRef(false);   // true while initial load animation is playing
    const entryAnimCountedRef = useRef(false);   // dedup guard for the global animation counter
    const deferReplayRef = useRef(false);        // expand-replay: wait for all entry animations
    const [entryAnimStage, setEntryAnimStage] = useState(0); // 0=idle, 1=stack, 2=headers, 3=bodies
    const entryAnimTimersRef = useRef([]);
    const [scrollEnabled, setScrollEnabled] = useState(false);
    const prevExpandedRef = useRef(new Set());
    const layoutMeasureTokenRef = useRef(0);
    const appliedMeasureTokenRef = useRef(-1);
    const hasAppliedTrailingSpaceRef = useRef(false);
    const noAnimationCalcRef = useRef(false);
    const lastCalcViewportWidthRef = useRef(0);
    // Dedup key for resize/zoom recalcs: `${dpr}x${width}x${height}`.
    // A single zoom change fires BOTH the ResizeObserver and the matchMedia DPR
    // listener; the browser sometimes dispatches the DPR `change` event after the
    // resize debounce has already executed, so the two no longer coalesce via the
    // shared resizeDebounceRef and the widget redraws twice. Guarding each recalc
    // with this key makes a recalc a no-op when the inputs (dpr + container dims)
    // are unchanged, collapsing the duplicate into one. A real zoom changes the
    // key, so it still recalculates.
    const lastRecalcKeyRef = useRef("");
    // Set by the resize/zoom paths when they change maxTicketsToRender, so the full-reset
    // the count change triggers stays SILENT (no entry-animation replay, no blank) instead
    // of being treated as a fresh initial load. Cleared when the full-reset consumes it.
    const resizeFullResetRef = useRef(false);
    // True once the widget has completed its one-time entry load. Used to keep every
    // subsequent re-init (zoom re-estimate, resize) silent so the entry animation never
    // replays, and to never shrink the rendered count on resize.
    const hasEverLoadedRef = useRef(false);
    // Live ref so closures created at mount (empty-deps effects) log the current widget id
    // instead of the stale id captured before data arrived.
    const widgetDbgIdRef = useRef("?");
    const normalizedCardsData = useMemo(() => (Array.isArray(cardsData) ? cardsData : []), [cardsData]);

    const defaultWidthOfOpenCard = 250 //290;
    const defaultOpenHeaderBlockPx = 26;
    const MIN_MEDIUM_HEADERS = 3;
    // Body width scales proportionally with widget height to maintain aspect ratio across resolutions.
    const OPEN_CARD_HEIGHT_RATIO = 0.83; // at ~301px tall → 250px (current default)
    // Quantise to 8-px steps so small height differences between two grid rows (CSS
    // sub-pixel rounding ≤ 1 px) never produce different dynamicOpenCardWidth values.
    // Without stepping, a 2 px height difference produces 1–2 px width difference, which
    // changes openFitCost and therefore safeMaxCards, causing two identical widgets to
    // render with different card counts.  Matches the portrait dynamicOpenCardHeight stepping.
    const OPEN_CARD_WIDTH_STEP_PX = 8;
    const dynamicOpenCardWidth = useMemo(() => {
        if (widgetViewportHeight <= 0) return defaultWidthOfOpenCard;
        const computed = Math.round(widgetViewportHeight * OPEN_CARD_HEIGHT_RATIO);
        return Math.max(OPEN_CARD_WIDTH_STEP_PX, Math.floor(computed / OPEN_CARD_WIDTH_STEP_PX) * OPEN_CARD_WIDTH_STEP_PX);
    }, [widgetViewportHeight]);

    const isOpenTicketPlacementTop = useMemo(
        () => String(OpenTicketPlacement || "bottom").toLowerCase() === "top",
        [OpenTicketPlacement]
    );
    // ── minOpenTicketCount semantics (0-99) ──────────────────────────────
    //   0      → render NO open cards at all (collapsed-only; nothing expanded).
    //   1-98   → render up to that many minimum open cards (clamped/floored
    //            into [1,98]).
    //   99     → "no maximum" — render as many open cards as fit the viewport
    //            (this is the legacy behavior previously triggered by 0).
    // Non-finite input falls back to 1 (legacy default "open at least one").
    const safeMinOpenCountForRender = useMemo(() => {
        const n = Number(minOpenTicketCount);
        if (!Number.isFinite(n)) return 1;
        const floored = Math.floor(n);
        if (floored <= 0) return 0;
        if (floored >= 99) return 99;
        return Math.max(1, Math.min(98, floored));
    }, [minOpenTicketCount]);
    const estimatedTicketsToRender = useMemo(() => {
        const totalTickets = normalizedCardsData.length;
        if (totalTickets === 0) return 0;

        // Account for grid margin (16px) + containerPadPx buffer (8px each side = 16px).
        // Must match calculateAndAdjustHeight: containerPadPx=8 → availableWidth = clientWidth - 16.
        const gridMarginPx = 16 + 16; // 16 grid margin + 16 containerPad (8 each side)
        const effectiveWidth = Math.max(0, widgetViewportWidth - gridMarginPx);

        // DOM pixel costs (priority-first model — matches calculateAndAdjustHeight):
        // Use DPR-corrected border so estimator matches actualCollapsedCardDomPx in the main calc.
        // Hardcoded 2px (DPR=1) causes safeMaxCards to over-count collapsed cards at non-1.0 DPR
        // (e.g. DPR=0.833 → actual border=2.4px, but estimator uses 2px → 44 cards allocated
        // when only 40 fit → last card overflows the container and gets cropped).
        const _estDprL = window.devicePixelRatio || 1;
        // Same DPR-floor fix as WidgetCards.js: clamp to min 1px so sub-integer
        // DPR values (e.g. 33% zoom → DPR≈0.33) don't collapse border to 0.
        const _estBorderL = Math.max(1, Math.round(_estDprL) / _estDprL);
        const headerBorderPx = 2 * _estBorderL;                                                        // DPR-corrected
        const bodyPaddingExtraPx = 12;
        const collapsedCardPx = 3 + headerBorderPx;                                                    // matches actualCollapsedCardDomPx
        // Border is already in collapsedCardPx — do NOT re-add headerBorderPx in the upgrade delta.
        const openCardPx = collapsedCardPx
            + (defaultOpenHeaderBlockPx - 3)
            + (dynamicOpenCardWidth + bodyPaddingExtraPx);                                              // matches openCardFullWidthPx
        const cardGapEstPx = isDarkTheme ? 5 : 3;

        const noMaxMode = safeMinOpenCountForRender === 99;
        const noOpenMode = safeMinOpenCountForRender === 0;

        // noOpenMode: no open cards at all — estimate purely on collapsed-card cost.
        if (noOpenMode) {
            if (effectiveWidth <= 0) return Math.max(1, Math.min(totalTickets, 1));
            const safeMaxCardsNoOpen = Math.floor(effectiveWidth / Math.max(1, collapsedCardPx));
            return Math.max(1, Math.min(totalTickets, safeMaxCardsNoOpen));
        }

        const minOpen = noMaxMode
            ? Math.min(totalTickets, Math.floor(effectiveWidth / Math.max(1, openCardPx)))
            : Math.min(safeMinOpenCountForRender, totalTickets);

        if (effectiveWidth <= 0) return minOpen;

        // Priority-first: find the largest open count where
        //   openWidth + nonOpenCollapsedBaseline <= effectiveWidth  (no overflow)
        // In noMaxMode, omit the nonOpenBaseline — we only require open cards to fit;
        // remaining space fills with as many collapsed cards as possible.
        // For regular mode, stop at safeMinOpenCountForRender — same fix as WidgetCards.js:
        // at extreme zoom-out totalTickets×collapsedPx can exceed availableWidth alone,
        // making the loop reduce fittedOpenCount to 0 and returning 1 card, which limits
        // targetOpenCount to 1 even when the container has room for the required min count.
        let fittedOpenCount = minOpen;
        const openCountFloor = noMaxMode ? 0 : safeMinOpenCountForRender;
        while (fittedOpenCount > openCountFloor) {
            const openWidth = fittedOpenCount * openCardPx
                + Math.max(0, fittedOpenCount - 1) * cardGapEstPx;
            const nonOpenBaseline = noMaxMode ? 0 : (totalTickets - fittedOpenCount) * collapsedCardPx;
            if (openWidth + nonOpenBaseline <= effectiveWidth) break;
            fittedOpenCount--;
        }

        if (fittedOpenCount === 0) return 1;

        // Priority: open → medium → collapsed.
        // After allocating open cards, reserve space for up to MIN_MEDIUM_HEADERS medium
        // upgrades before filling the rest with collapsed. Without this, ALL remaining
        // space after open cards is allocated to collapsed, leaving 0 budget for medium
        // when applyCompressedModes runs (e.g. 7 open leaves only 1.2px → 0 medium fit).
        //
        // Each medium card costs (normalHeaderMin - collapsedMin + gap) = 23 + cardGap above
        // a collapsed card. We reserve for up to MIN_MEDIUM_HEADERS medium upgrades so the
        // main calc has budget to promote that many collapsed → medium.
        // Priority: open → medium → collapsed.
        // Subtract MIN_MEDIUM_HEADERS * mediumUpgrade from the collapsed budget so that
        // applyCompressedModes has room to promote some collapsed → medium after open cards
        // are placed.  The reserved space (84px) is NOT added to safeMaxCards as extra
        // medium slots — it just reduces maxCollapsed so the main calc has budget for medium.
        const _medUpgradeEst = (defaultOpenHeaderBlockPx - 3) + cardGapEstPx; // ≈ 28px
        const _medReserveEst = Math.min(3, Math.max(0, totalTickets - fittedOpenCount)) * _medUpgradeEst;
        const openWidth = fittedOpenCount * openCardPx
            + Math.max(0, fittedOpenCount - 1) * cardGapEstPx;
        const remainingForCollapsed = Math.max(0, effectiveWidth - openWidth - _medReserveEst);
        const maxCollapsed = Math.floor(remainingForCollapsed / collapsedCardPx);
        const safeMaxCards = fittedOpenCount + maxCollapsed;

        return Math.max(fittedOpenCount, Math.min(totalTickets, safeMaxCards));
    }, [normalizedCardsData.length, safeMinOpenCountForRender, widgetViewportWidth, isDarkTheme, dynamicOpenCardWidth]);

    const [maxTicketsToRender, setMaxTicketsToRender] = useState(estimatedTicketsToRender);

    // Apply the estimator's guess ONLY before the first real layout calc (mirrors
    // WidgetCards.js).  After initial load, maxTicketsToRender is managed solely by
    // calculateAndAdjustHeight (Step 2 / trim) and the resize-debounce reset below.
    // Without this guard, every dimension change (viewport width, DPR) that updates
    // estimatedTicketsToRender fires a full-reset with animation, which then races
    // the 200 ms silent-calc debounce — causing mid-animation measurements and wrong
    // card counts that diverge from page-refresh results.
    useEffect(() => {
        if (hasInitiallyLoadedRef.current) return;
        setMaxTicketsToRender((prev) => {
            const boundedEstimate = Math.max(
                safeMinOpenCountForRender,
                Math.min(normalizedCardsData.length, estimatedTicketsToRender)
            );
            // On a zoom/resize-driven re-init (widget already loaded once), never SHRINK
            // the count — only grow it. The calc keeps all cards visible, so shrinking to
            // an under-counting estimate just flickers the ticket count down and back up
            // mid-resize. Also mark the resulting full-reset silent (no animation replay).
            const target = hasEverLoadedRef.current
                ? Math.max(prev, boundedEstimate)
                : boundedEstimate;
            if (target === prev) return prev;
            if (hasEverLoadedRef.current) {
                resizeFullResetRef.current = true;
            }
            return target;
        });
    }, [estimatedTicketsToRender, normalizedCardsData.length, safeMinOpenCountForRender]);

    // Keep refs to the latest estimator and maxTickets values so the resize debounce
    // can read them without a stale closure.  Updated on every render.
    const estimatedTicketsToRenderRef = useRef(estimatedTicketsToRender);
    estimatedTicketsToRenderRef.current = estimatedTicketsToRender;
    widgetDbgIdRef.current = String(normalizedCardsData[0]?.title || normalizedCardsData[0]?.id || "?").slice(0, 20);
    const maxTicketsToRenderRef = useRef(maxTicketsToRender);
    maxTicketsToRenderRef.current = maxTicketsToRender;

    const displayCardsData = useMemo(
        () => normalizedCardsData.slice(0, maxTicketsToRender),
        [normalizedCardsData, maxTicketsToRender]
    );
    const themeStyles = useMemo(() => ({
        cardBackground: isDarkTheme ? "rgb(52, 52, 52)" : "#ffffff",
        cardBorder: isDarkTheme ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.12)",
        cardBoxShadow: isDarkTheme ? "0 2px 8px rgba(0,0,0,0.60)" : "none",
        bodyTextColor: isDarkTheme ? "#d0d0d0" : "inherit",
        mutedTextColor: isDarkTheme ? "#a8a8a8" : "#6c757d",
    }), [isDarkTheme]);

    const [cardStates, setCardStates] = useState(
        displayCardsData.map((_, index) => {
            // When safeMinOpenCountForRender === 0, NO card should be auto-expanded —
            // every card renders collapsed (no open/expanded cards at all).
            const shouldAutoOpenFirst = safeMinOpenCountForRender !== 0 && index === 0;
            return {
                isExpanded: shouldAutoOpenFirst,
                isHalfOpen: false,
                fullHeight: 0,  // Store full height for accurate half-open calculation
                visibleBodyHeight: 0,
                renderMode: shouldAutoOpenFirst ? "open" : "collapsed",
                priorCompressedMode: "collapsed",
            };
        })
    );
    const [isReady, setIsReady] = useState(false);  // Hide cards until first calc completes
    const [disableBodyTransition, setDisableBodyTransition] = useState(true);
    const [disableHeaderTransition, setDisableHeaderTransition] = useState(true);

    // Keep open cards' body WIDTH in lock-step with the live dynamicOpenCardWidth during
    // resize/zoom, WITHOUT waiting for the ~200ms-debounced recalc. dynamicOpenCardWidth
    // updates synchronously when widgetViewportHeight changes, but an open card's rendered
    // width comes from cardState.visibleBodyHeight (reused as width here) — which only the
    // debounced calc refreshes. In the gap the open ticket renders at its stale, pre-zoom
    // width. Patch open cards here immediately. Gated on hasEverLoadedRef so it never
    // interferes with the one-time entry animation; no-op unless a width actually differs.
    useLayoutEffect(() => {
        if (!hasEverLoadedRef.current) return;
        const w = Math.floor(dynamicOpenCardWidth);
        if (!(w > 0)) return;
        setCardStates((prev) => {
            let changed = false;
            const next = prev.map((s) => {
                if (s.renderMode === "open" && s.visibleBodyHeight !== w) {
                    changed = true;
                    return { ...s, fullHeight: w, visibleBodyHeight: w };
                }
                return s;
            });
            return changed ? next : prev;
        });
    }, [dynamicOpenCardWidth]);

    const isRevertAnimatingRef = useRef(false);
    const revertAnimationTimerRef = useRef(null);
    const hasInitiallyLoadedRef = useRef(false);  // Track if we've loaded at least once
    const isMeasuringRef = useRef(false);  // Track if we're in the measurement phase specifically
    const step2ExpansionRef = useRef(false); // Step 2 added more cards during resize — skip full-reset
    const [imageErrors, setImageErrors] = useState({});  // Track image load errors by card id
    const [compactStatusStyle, setCompactStatusStyle] = useState({
        headerPadPx: 4,
        headerMinWidthPx: 10,
        statusPadPx: 2,
        headerFontRem: 0.75,
        normalHeaderFontRem: 0.90,
        statusLabelRem: 0.8,
        statusValueRem: 1,
        bodyPaddingPx: 5,
        avatarSizePx: 41,
        avatarFontPx: 15,
        ownerFontRem: 0.78,
        topLeftFontRem: 0.90,
        titleFontRem: 1.30,
        descFontRem: 0.83,
        metaFontRem: 0.70,
        titleLines: 1,
        descLines: 1,
        containerPadPx: 0,
        avatarWrapWidthPx: 67,
        showHeaderText: true,
        headerTopPadPx: 6,
        titleMarginBottomRem: 0.15,
        descMarginBottomRem: 0.40,
        metaRowPadPx: 4,
    });

    useLayoutEffect(() => {
        const layoutDepsChanged =
            prevLayoutDepsRef.current.minOpenTicketCount !== minOpenTicketCount ||
            prevLayoutDepsRef.current.isOpenTicketPlacementTop !== isOpenTicketPlacementTop;
        const prevData = prevDisplayCardsRef.current;
        prevDisplayCardsRef.current = displayCardsData;
        prevLayoutDepsRef.current = { minOpenTicketCount, isOpenTicketPlacementTop };

        if (hasInitiallyLoadedRef.current && !layoutDepsChanged && areSameCards(prevData, displayCardsData)) {
            return;
        }

        // ── Step 2 resize expansion — skip full reset ─────────────────────────
        // When Step 2 adds more cards after initial load it sets step2ExpansionRef
        // and returns early (without setCardStates/setIsReady).  Detect it here by
        // checking that prevData is an exact prefix of the new displayCardsData.
        const isPureStep2Expansion = (
            step2ExpansionRef.current &&
            hasInitiallyLoadedRef.current &&
            !layoutDepsChanged &&
            (
                areSameCards(prevData, displayCardsData) ||
                (
                    prevData.length < displayCardsData.length &&
                    areSameCards(prevData, displayCardsData.slice(0, prevData.length))
                )
            )
        );
        if (isPureStep2Expansion) {
            step2ExpansionRef.current = false;
            cardRefs.current = new Array(displayCardsData.length);
            setCardStates(prev => {
                if (prev.length >= displayCardsData.length) return prev;
                const extra = displayCardsData.slice(prev.length).map(() => ({
                    isExpanded: false, isHalfOpen: false, fullHeight: 0,
                    visibleBodyHeight: 0, renderMode: "collapsed", priorCompressedMode: "collapsed",
                }));
                return [...prev, ...extra];
            });
            calcRunIdRef.current += 1;
            requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
            return;
        }

        // Was this reset triggered by a zoom/resize (vs. a genuine data/config change)?
        // If so, keep it silent: the resize path already set noAnimationCalcRef=true, and
        // we must NOT clear it here or blank the widget below — otherwise the zoom replays
        // the entry animation (and, at high zoom, the animated expand-replay → double draw).
        const silentResizeReset = resizeFullResetRef.current;
        resizeFullResetRef.current = false;

        // Clear stale resize/animation flags so the upcoming animation calc is not
        // silently swallowed by a leftover noAnimationCalcRef=true from a previous
        // resize that was co-batched with this config/data change.
        step2ExpansionRef.current = false;
        if (!silentResizeReset) {
            noAnimationCalcRef.current = false;
        }


        // Increment data version to invalidate any pending auto-reverts
        dataVersionRef.current += 1;

        initialStateRef.current = null;
        hasUserInteractedRef.current = false;
        hasAppliedTrailingSpaceRef.current = false;
        isFirstCalculationRef.current = true;
        calcRetryRef.current = 0;
        calcRunIdRef.current += 1;
        cardRefs.current = new Array(displayCardsData.length);
        entryAnimTimersRef.current.forEach(clearTimeout);
        entryAnimTimersRef.current = [];
        setEntryAnimStage(0);

        // Clear any pending auto-revert timer when data changes
        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
            autoRevertTimerRef.current = null;
        }

        // Reset image errors when data changes
        setImageErrors({});

        if (displayCardsData.length === 0) {
            setCardStates([]);
            // Do NOT call setIsReady(true) here — same background-flash reason as WidgetCards.js.
            // Do NOT set hasInitiallyLoadedRef.current = true here — see Step 2 comment in calc.
            setDisableBodyTransition(false);
            return;
        }

        // Deterministic refresh: always reset and recalculate to avoid stale layout/state
        isCalculatingRef.current = true;
        // On a zoom-driven reset keep the widget visible — blanking it (setIsReady(false))
        // is what makes the cards disappear and pop back in on every zoom step. The silent
        // calc that follows re-applies the final layout in place.
        if (!silentResizeReset) {
            setIsReady(false);
        }
        setDisableBodyTransition(true);
        setDisableHeaderTransition(true);
        const _collapsedCardState = {
            isExpanded: false,
            isHalfOpen: false,
            fullHeight: 0,
            visibleBodyHeight: 0,
            renderMode: "collapsed",
            priorCompressedMode: "collapsed",
        };
        if (silentResizeReset) {
            // Preserve existing card visuals (padded/truncated to the new count) so the
            // widget doesn't flash all-collapsed between this reset and the silent calc.
            // Refresh any open card's body WIDTH to the current dynamicOpenCardWidth so it
            // doesn't show its stale, pre-zoom width during the gap before the silent calc.
            const _openW = Math.floor(dynamicOpenCardWidth);
            setCardStates((prev) =>
                displayCardsData.map((_, index) => {
                    const p = prev[index];
                    if (!p) return _collapsedCardState;
                    if (p.renderMode === "open") {
                        return { ...p, fullHeight: _openW, visibleBodyHeight: _openW };
                    }
                    return p;
                })
            );
        } else {
            setCardStates(
                displayCardsData.map(() => ({ ..._collapsedCardState }))
            );
        }

        // Skip the entry animation when the initial card count is large (extreme zoom-out).
        // Overflow-bound widgets (estimator count below total) end in an expand-replay
        // and usually a silent render — defer their heavy first calc until no parallel
        // widget is animating.  deferReplayRef marks the replay leg of that pipeline.
        const deferHeavyFirstCalc =
            (!hasInitiallyLoadedRef.current &&
                displayCardsData.length < normalizedCardsData.length) ||
            deferReplayRef.current;

        const scheduleCalc = (delayMs = 0) => {
            const run = () => {
                const doCalc = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
                const start = deferHeavyFirstCalc
                    ? () => { deferReplayRef.current = false; _whenNoEntryAnimations(doCalc); }
                    : doCalc;
                if (document.fonts && document.fonts.ready) {
                    document.fonts.ready.then(start).catch(start);
                } else {
                    start();
                }
            };
            if (delayMs > 0) return setTimeout(run, delayMs);
            run();
            return null;
        };

        // Stagger first-load calc so parallel widgets don't all race in the same frame.
        // A zoom-driven silent reset isn't a first load — run it promptly so zoom stays
        // responsive (the stagger delay is only to space out entry animations).
        const timer = scheduleCalc(
            (hasInitiallyLoadedRef.current || silentResizeReset) ? 0 : _getWidgetAnimStagger()
        );

        return () => {
            if (timer) clearTimeout(timer);
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
                autoRevertTimerRef.current = null;
            }
            if (revertAnimationTimerRef.current) {
                clearTimeout(revertAnimationTimerRef.current);
                revertAnimationTimerRef.current = null;
            }
            isRevertAnimatingRef.current = false;
            isEntryAnimatingRef.current = false;
            _entryAnimEnded(entryAnimCountedRef);
            entryAnimTimersRef.current.forEach(clearTimeout);
            entryAnimTimersRef.current = [];
        };
    }, [displayCardsData, minOpenTicketCount, isOpenTicketPlacementTop]);

    useEffect(() => {
        // Recalculate after images and fonts load to stabilize first render vs refresh
        const triggerCalc = () => {
            const run = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(run).catch(run);
            } else {
                run();
            }
        };

        const timer = setTimeout(() => {
            if (!hasInitiallyLoadedRef.current) {
                triggerCalc();
            }
        }, 300);

        const images = parentRef.current
            ? parentRef.current.querySelectorAll(".card img")
            : [];
        const handleImageLoad = () => triggerCalc();

        images.forEach((img) => {
            if (!img.complete) {
                img.addEventListener("load", handleImageLoad);
                img.addEventListener("error", handleImageLoad);
            }
        });

        return () => {
            clearTimeout(timer);
            images.forEach((img) => {
                img.removeEventListener("load", handleImageLoad);
                img.removeEventListener("error", handleImageLoad);
            });
        };
    }, [displayCardsData]);

    useLayoutEffect(() => {
        if (!parentRef.current) return;

        const scheduleResizeCalc = () => {
            // Do NOT skip during entry animation — zoom changes while the animation plays
            // must still update the layout.  The silent calc (noAnimationCalcRef=true)
            // cancels the animation via calcRunIdRef and applies the correct layout.
            if (resizeDebounceRef.current) clearTimeout(resizeDebounceRef.current);
            resizeDebounceRef.current = setTimeout(() => {
                // Re-run the same complete initialisation as a page refresh:
                // reset maxTicketsToRender to the estimator's value for the new
                // dimensions.  This triggers the full-reset path (identical to
                // page refresh).  If the count is unchanged, fall back to a
                // plain silent recalc so new dimensions still take effect.
                // Skip if a recalc already ran for this exact (dpr, w, h) — prevents
                // the double redraw when the ResizeObserver and DPR listener both fire.
                const _key = `${(window.devicePixelRatio || 1).toFixed(3)}x${parentRef.current?.clientWidth || 0}x${parentRef.current?.clientHeight || 0}`;
                if (_key === lastRecalcKeyRef.current) {
                    resizeDebounceRef.current = null;
                    return;
                }
                lastRecalcKeyRef.current = _key;
                const newEstimate = estimatedTicketsToRenderRef.current;
                const prevMax = maxTicketsToRenderRef.current;
                // Never SHRINK the count on a resize/zoom re-init — the calc keeps all cards
                // visible, so dropping to an under-counting estimate only flickers the count
                // down and back up. Only grow; mark the full-reset silent when it grows.
                const target = Math.max(prevMax, newEstimate);
                hasInitiallyLoadedRef.current = false;
                noAnimationCalcRef.current = true;
                isFirstCalculationRef.current = true;
                if (target !== prevMax) {
                    resizeFullResetRef.current = true;
                    setMaxTicketsToRender(target);
                } else {
                    calcHandlerRef.current?.();
                }
                resizeDebounceRef.current = null;
            }, 200);
        };

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(() => scheduleResizeCalc());
            observer.observe(parentRef.current);
            return () => observer.disconnect();
        }

        window.addEventListener("resize", scheduleResizeCalc);
        return () => window.removeEventListener("resize", scheduleResizeCalc);
    }, []);

    // Browser zoom changes window.devicePixelRatio (and therefore the rendered
    // border/sub-pixel widths the calc depends on) WITHOUT changing clientWidth/
    // clientHeight — so neither the ResizeObserver nor the widgetViewportWidth
    // effect below ever fires, leaving a stale layout that renders cropped at the
    // new zoom level (e.g. open cards overflow the container at DPR=0.417/240%).
    // matchMedia(`(resolution: ...dppx)`) is the standard way to observe DPR
    // changes; re-register for the new ratio each time it fires.
    useEffect(() => {
        if (typeof window.matchMedia !== "function") return undefined;

        let mql = null;
        const handleDprChange = () => {
            const newDpr = window.devicePixelRatio || 1;
            if (newDpr !== lastDprRef.current) {
                lastDprRef.current = newDpr;
                if (resizeDebounceRef.current) clearTimeout(resizeDebounceRef.current);
                resizeDebounceRef.current = setTimeout(() => {
                    // Skip if a recalc already ran for this exact (dpr, w, h).
                    const _key = `${(window.devicePixelRatio || 1).toFixed(3)}x${parentRef.current?.clientWidth || 0}x${parentRef.current?.clientHeight || 0}`;
                    if (_key === lastRecalcKeyRef.current) {
                        resizeDebounceRef.current = null;
                        return;
                    }
                    lastRecalcKeyRef.current = _key;
                    const newEstimate = estimatedTicketsToRenderRef.current;
                    const prevMax = maxTicketsToRenderRef.current;
                    // Never SHRINK the count on a resize/zoom re-init — only grow; mark the
                    // full-reset silent when it grows (see resizeFullResetRef).
                    const target = Math.max(prevMax, newEstimate);
                    hasInitiallyLoadedRef.current = false;
                    noAnimationCalcRef.current = true;
                    isFirstCalculationRef.current = true;
                    if (target !== prevMax) {
                        resizeFullResetRef.current = true;
                        setMaxTicketsToRender(target);
                    } else {
                        calcHandlerRef.current?.();
                    }
                    resizeDebounceRef.current = null;
                }, 200);
            }
            register();
        };
        const register = () => {
            if (mql) mql.removeEventListener("change", handleDprChange);
            mql = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
            mql.addEventListener("change", handleDprChange);
        };
        register();

        return () => {
            if (mql) mql.removeEventListener("change", handleDprChange);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const handleWheel = (e) => {
            if (e.deltaY === 0) return;
            e.preventDefault();
            el.scrollLeft += e.deltaY;
        };
        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, []);

    // Trigger a silent recalculation when the viewport dimensions change after the
    // initial load.  Guards:
    //   - hasInitiallyLoadedRef: skip before first load (full anim calc handles that)
    //   - isEntryAnimatingRef:   skip while animation is playing
    //   - isFirstCalculationRef: skip when a full recalc is already pending (data change
    //                            race — useLayoutEffect sets this before its scheduleCalc)
    //   - lastCalcViewportWidthRef: skip if width hasn't actually changed
    useEffect(() => {
        if (!hasInitiallyLoadedRef.current) return;
        if (isFirstCalculationRef.current) return;
        if (widgetViewportWidth === lastCalcViewportWidthRef.current) return;
        lastCalcViewportWidthRef.current = widgetViewportWidth;

        if (resizeDebounceRef.current) clearTimeout(resizeDebounceRef.current);
        resizeDebounceRef.current = setTimeout(() => {
            // Skip if a recalc already ran for this exact (dpr, w, h).
            const _key = `${(window.devicePixelRatio || 1).toFixed(3)}x${parentRef.current?.clientWidth || 0}x${parentRef.current?.clientHeight || 0}`;
            if (_key === lastRecalcKeyRef.current) {
                resizeDebounceRef.current = null;
                return;
            }
            lastRecalcKeyRef.current = _key;
            const newEstimate = estimatedTicketsToRenderRef.current;
            const prevMax = maxTicketsToRenderRef.current;
            // Never SHRINK the count on a resize/zoom re-init — only grow; mark the
            // full-reset silent when it grows (see resizeFullResetRef).
            const target = Math.max(prevMax, newEstimate);
            hasInitiallyLoadedRef.current = false;
            noAnimationCalcRef.current = true;
            isFirstCalculationRef.current = true;
            if (target !== prevMax) {
                resizeFullResetRef.current = true;
                setMaxTicketsToRender(target);
            } else {
                calcHandlerRef.current?.();
            }
            resizeDebounceRef.current = null;
        }, 200);
    }, [widgetViewportWidth, widgetViewportHeight]);

    useEffect(() => {
        calcHandlerRef.current = calculateAndAdjustHeight;
    });

    useLayoutEffect(() => {
        if (!isReady || displayCardsData.length === 0) return;

        const token = layoutMeasureTokenRef.current;
        if (token === appliedMeasureTokenRef.current) return;

        let raf1 = 0;
        let raf2 = 0;
        let settleTimer = null;

        const measureAndApply = () => {
            // Mark this layout token as processed. No trailing-space redistribution —
            // open cards stay at their calculated fixed width (defaultWidthOfOpenCard).
            appliedMeasureTokenRef.current = token;
        };

        // CSS keyframe animations are immune to React re-renders — safe to measure
        // at normal settle time without disrupting the running animation.
        const settleDelayMs = disableBodyTransition ? 150 : 260;
        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                if (settleDelayMs > 0) {
                    settleTimer = setTimeout(measureAndApply, settleDelayMs);
                } else {
                    measureAndApply();
                }
            });
        });

        return () => {
            if (raf1) cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
            if (settleTimer) clearTimeout(settleTimer);
        };
    }, [cardStates, isReady, displayCardsData.length, disableBodyTransition]);

    // One-shot width correction: when a card transitions to open after a user toggle,
    // measure its real scrollWidth and grow visibleBodyHeight (used as flexBasis) if
    // the pre-calculated estimate is too small. Skipped on initial load.
    useLayoutEffect(() => {
        if (!isReady || !hasUserInteractedRef.current) return;
        const currentExpanded = new Set(
            cardStates.flatMap((s, i) => s.isExpanded ? [i] : [])
        );
        const newlyOpened = [...currentExpanded].filter(i => !prevExpandedRef.current.has(i));
        prevExpandedRef.current = currentExpanded;
        if (newlyOpened.length === 0) return;
        const fixes = {};
        newlyOpened.forEach(i => {
            const body = cardRefs.current[i]?.querySelector(".card-body");
            if (!body) return;
            const naturalWidth = body.scrollWidth;
            if (naturalWidth > Number(cardStates[i]?.visibleBodyHeight || 0) + 2) fixes[i] = naturalWidth;
        });
        if (Object.keys(fixes).length === 0) return;
        layoutMeasureTokenRef.current += 1;
        setCardStates(prev => prev.map((s, i) =>
            fixes[i] !== undefined && s.isExpanded
                ? { ...s, visibleBodyHeight: fixes[i], fullHeight: fixes[i] }
                : s
        ));
    }, [cardStates, isReady]);

    const calculateAndAdjustHeight = () => {
        // Consume the skip-animation flag immediately so it is always cleared,
        // even when we take an early-return path below.
        const skipAnimation = noAnimationCalcRef.current;
        noAnimationCalcRef.current = false;
        // Set to true when all cards in collapsed mode overflow the viewport.
        // Triggers scroll instead of trimming the card count.
        let overflowScroll = false;

        // For a full-animation recalc, reset the viewport baseline so the
        // viewport-prop useEffect doesn't fire a redundant silent recalc afterwards.
        if (!skipAnimation) {
            lastCalcViewportWidthRef.current = widgetViewportWidth;
        }

        if (displayCardsData.length === 0) {
            // No cards — stay hidden. setIsReady(true) omitted for same reason as WidgetCards.js.
            setDisableBodyTransition(false);
            return;
        }

        // Allow first calculation to always run, then throttle subsequent ones
        const now = Date.now();
        if (!isFirstCalculationRef.current && now - lastCalcTimeRef.current < 400) return;

        if (!parentRef.current || cardRefs.current.length === 0) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isFirstCalculationRef.current = false; // Mark first calculation as done
        lastCalcTimeRef.current = now;


        const parentWidth = parentRef.current.clientWidth;
        const cardElements = cardRefs.current.slice(0, displayCardsData.length);

        if (cardElements.length === 0 || cardElements.some((el) => !el)) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isCalculatingRef.current = true;
        isMeasuringRef.current = true;
        setDisableBodyTransition(true);



        // Wait a tick for layout to recalculate
        const currentCalcRunId = ++calcRunIdRef.current;
        requestAnimationFrame(() => {

            const restoreMeasuredBodies = () => {
                // originalHeights.forEach((item) => {
                //     if (item) {
                //         item.body.style.maxHeight = item.original.maxHeight;
                //         item.body.style.display = item.original.display;
                //         item.body.style.overflow = item.original.overflow;
                //         item.body.style.padding = item.original.padding;
                //         item.body.style.margin = item.original.margin;
                //         item.body.style.visibility = item.original.visibility;
                //         item.body.style.position = item.original.position;
                //         item.body.style.left = item.original.left;
                //         item.body.style.right = item.original.right;
                //         item.body.style.width = item.original.width;
                //         item.body.style.pointerEvents = item.original.pointerEvents;
                //     }
                // });
            };

            if (currentCalcRunId !== calcRunIdRef.current) {
                //restoreMeasuredBodies();
                isMeasuringRef.current = false;
                setDisableBodyTransition(false);
                // Same recovery logic as WidgetCards.js: when rapid zoom changes cause
                // every inner RAF to be cancelled, the widget stays hidden forever.
                // Schedule a recovery pass if no other calc is already in flight.
                if (!hasInitiallyLoadedRef.current || isCalculatingRef.current) {
                    return;
                }
                setTimeout(() => {
                    if (currentCalcRunId === calcRunIdRef.current) {
                        noAnimationCalcRef.current = true;
                        isFirstCalculationRef.current = true;
                        calcHandlerRef.current?.();
                    }
                }, 0);
                return;
            }

            // In landscape mode cards are arranged horizontally. Spacing between cards
            // comes from marginRight, not gap/marginBottom. Collapsed cards have 0px
            // marginRight; only medium/open non-last cards have 3-5px. At this point all
            // cards are in the collapsed reset state, so actual spacing is 0px.
            // Reading styles.gap (a container property) from individual card elements
            // always returns "" which falls back to "3.5" — that was wrong. Use 0 here
            // so the collapsed baseline doesn't inflate the budget calculation.
            const cardSpacing = cardElements.map(() => 0);

            // Calculate layout - use actual rendered grid height to avoid drift from extra paddings/offsets
            const scrollContainer = parentRef.current.querySelector(".slim-scroll, .slim-scroll-dark");
            //const containerWidth = gridRef.current?.clientHeight || (scrollContainer ? scrollContainer.clientHeight : parentHeight);
            const containerWidth = gridRef.current?.clientWidth || (scrollContainer ? scrollContainer.clientWidth : parentWidth);
            const primaryOpenBodyPx = dynamicOpenCardWidth;

            // Measure all card body heights
            const cardHeights = cardElements.map((card) => {
                //const body = card.querySelector(".card-body");
                return primaryOpenBodyPx;
                //return body ? body.scrollWidth : 0;
            });

            // Restore original display state
            restoreMeasuredBodies();

            // Measurement phase is complete.
            // Keep disableBodyTransition = true so the initial layout render has no
            // flex-basis animation. This ensures measureAndApply reads settled DOM widths
            // (not mid-animation values) when it fires 150ms later.
            // disableBodyTransition is set to false in toggleCard on first user interaction.
            isMeasuringRef.current = false;



            // Dynamically compress header/status area based on available width per card
            // containerPadPx = 8: reserve 8px on each side (16px total) as a visual edge buffer.
            // The grid has margin: 0 8px but clientWidth already excludes the margin.
            // Without this buffer the rightmost collapsed stack-header touches the container
            // border (sub-pixel rendering and scroll-bar appearance consume the last few pixels).
            // Portrait uses the same containerPadPx=8 convention.
            const containerPadPx = 8;
            const availableWidth = containerWidth - (containerPadPx * 2);
            const perCardHeight = availableWidth / Math.max(cardElements.length, 1);
            const compressionRatio = Math.max(0, Math.min(1, (90 - perCardHeight) / 60));
            const roomyRatio = 1 - compressionRatio;

            const headerPadPx = 2;  //Math.max(0, Math.round(4 * roomyRatio));
            const headerMinWidthPx = Math.max(2, Math.round(3 + (11 * roomyRatio)));
            const statusPadPx = Math.max(0, Math.round(4 * roomyRatio));
            const headerFontRem = Number((0.56 + (0.26 * roomyRatio)).toFixed(2));

            // Status text - keep standard sizes for clear readability
            const statusLabelRem = 0.8;
            const statusValueRem = 1;

            // Responsive scaling anchored to the aspect-ratio reference point.
            // Reference height (482 px) is where dynamicOpenCardWidth ≈ 400 px (= 400 ÷ 0.83),
            // the point at which landscape font sizes were originally designed to reach their maximum.
            // No upper clamp — beyond 482 px fonts keep growing at the same rate as card width,
            // so visual density stays constant across all zoom levels.
            const heightScale = Math.max(0, (widgetViewportHeight - 150) / (482 - 150));
            const bodyPaddingPx = Math.round(3 + 3 * heightScale);             // 3 → 6 px
            const avatarSizePx = Math.round(32 + 18 * heightScale);            // 32 → 50 px
            const avatarFontPx = Math.round(12 + 6 * heightScale);             // 12 → 18 px
            const avatarWrapWidthPx = Math.round(54 + 26 * heightScale);       // 54 → 80 px
            const ownerFontRem = Number((0.70 + 0.15 * heightScale).toFixed(2)); // 0.70 → 0.85
            const topLeftFontRem = Number((0.80 + 0.20 * heightScale).toFixed(2)); // 0.80 → 1.00
            const titleFontRem = Number((1.10 + 0.40 * heightScale).toFixed(2));   // 1.10 → 1.50
            const descFontRem = Number((0.75 + 0.15 * heightScale).toFixed(2));   // 0.75 → 0.90
            const metaFontRem = Number((0.65 + 0.10 * heightScale).toFixed(2));   // 0.65 → 0.75
            const titleLines = 1;
            const descLines = 1;
            const showHeaderText = true;
            const normalHeaderFontRem = Number((0.72 + 0.18 * heightScale).toFixed(2)); // 0.72→0.90
            const headerTopPadPx = Math.max(2, Math.round(2 + 4 * heightScale));         // 2→6 px
            const titleMarginBottomRem = Number((0.10 + 0.05 * heightScale).toFixed(2)); // 0.10→0.15
            const descMarginBottomRem = Number((0.25 + 0.15 * heightScale).toFixed(2));  // 0.25→0.40
            const metaRowPadPx = Math.round(2 + 4 * heightScale);                        // 2→6 px

            const newCardStates = displayCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: cardHeights[index] || 0,  // Store full height
                visibleBodyHeight: 0,
                renderMode: "collapsed",
                priorCompressedMode: "collapsed",
            }));


            // ── minOpenTicketCount semantics (0-99) ──────────────────────
            //   0    → render NO open cards (collapsed-only).
            //   1-98 → minimum number of open cards (clamped/floored).
            //   99   → "no maximum" — open as many as fit (legacy 0-behavior).
            const safeMinOpenCount = (() => {
                const n = Number(minOpenTicketCount);
                if (!Number.isFinite(n)) return 1;
                const floored = Math.floor(n);
                if (floored <= 0) return 0;
                if (floored >= 99) return 99;
                return Math.max(1, Math.min(98, floored));
            })();
            // noMaxMode: render as many open cards as fit (formerly safeMinOpenCount===0).
            // noOpenMode: render zero open cards — everything stays collapsed.
            const noMaxMode = safeMinOpenCount === 99;
            const noOpenMode = safeMinOpenCount === 0;

            const totalSpacing = cardSpacing.slice(0, -1).reduce((sum, spacing) => sum + spacing, 0);
            setCompactStatusStyle((prev) => {
                if (
                    prev.headerPadPx === headerPadPx &&
                    prev.headerMinWidthPx === headerMinWidthPx &&
                    prev.statusPadPx === statusPadPx &&
                    prev.headerFontRem === headerFontRem &&
                    prev.normalHeaderFontRem === normalHeaderFontRem &&
                    prev.statusLabelRem === statusLabelRem &&
                    prev.statusValueRem === statusValueRem &&
                    prev.bodyPaddingPx === bodyPaddingPx &&
                    prev.avatarSizePx === avatarSizePx &&
                    prev.avatarFontPx === avatarFontPx &&
                    prev.ownerFontRem === ownerFontRem &&
                    prev.topLeftFontRem === topLeftFontRem &&
                    prev.titleFontRem === titleFontRem &&
                    prev.descFontRem === descFontRem &&
                    prev.metaFontRem === metaFontRem &&
                    prev.titleLines === titleLines &&
                    prev.descLines === descLines &&
                    prev.containerPadPx === containerPadPx &&
                    prev.avatarWrapWidthPx === avatarWrapWidthPx &&
                    prev.showHeaderText === showHeaderText &&
                    prev.headerTopPadPx === headerTopPadPx &&
                    prev.titleMarginBottomRem === titleMarginBottomRem &&
                    prev.descMarginBottomRem === descMarginBottomRem &&
                    prev.metaRowPadPx === metaRowPadPx
                ) {
                    return prev;
                }
                return {
                    headerPadPx,
                    headerMinWidthPx: headerMinWidthPx,
                    statusPadPx,
                    headerFontRem,
                    normalHeaderFontRem,
                    statusLabelRem,
                    statusValueRem,
                    bodyPaddingPx,
                    avatarSizePx,
                    avatarFontPx,
                    ownerFontRem,
                    topLeftFontRem,
                    titleFontRem,
                    descFontRem,
                    metaFontRem,
                    titleLines,
                    descLines,
                    containerPadPx,
                    avatarWrapWidthPx,
                    showHeaderText,
                    headerTopPadPx,
                    titleMarginBottomRem,
                    descMarginBottomRem,
                    metaRowPadPx,
                };
            });

            const normalHeaderPadPxForCalc = 0;
            const normalHeaderMinWidthPxForCalc = defaultOpenHeaderBlockPx; // 26px — matches render normalHeaderMinWidthPx
            const mediumHeaderPadPxForCalc = normalHeaderPadPxForCalc;
            const mediumHeaderMinWidthPxForCalc = normalHeaderMinWidthPxForCalc; // 26px — matches render mediumHeaderMinWidthPx
            const collapsedHeaderPadPxForCalc = 0;
            // Must match render: collapsedHeaderMinWidthPx = 3 (line ~1226).
            // The old value of 4 caused the collapsed baseline to be over-estimated,
            // shrinking the available budget for open / medium cards.
            const collapsedHeaderMinWidthPxForCalc = 3;

            const normalHeaderBlockPxForCalc = normalHeaderMinWidthPxForCalc + (normalHeaderPadPxForCalc * 2);
            const mediumHeaderBlockPxForCalc = mediumHeaderMinWidthPxForCalc + (mediumHeaderPadPxForCalc * 2);
            const collapsedHeaderBlockPxForCalc = collapsedHeaderMinWidthPxForCalc + (collapsedHeaderPadPxForCalc * 2);

            // Actual DOM widths: header borders (1px each side) add outside stated minWidth (content-box
            // behavior), and body padding (6px each side) adds outside the flex-basis.
            // DELTAS between modes are unaffected (border cancels); only the baseline changes.
            // At sub-integer DPR (e.g. 125% zoom → DPR=1.25), a 1px CSS border renders as
            // Math.round(1 * dpr) / dpr = 0.8px. Using 1.0 over-estimates by 0.4px per card,
            // which accumulates to ~90px over 226 cards — exactly the observed trailing space.
            // ── DOM-based dimension measurement (landscape) ──────────────────────
            // Landscape cards are arranged horizontally. The collapsed card width comes
            // from the card header's min-width (3px CSS) plus its left+right border.
            //
            // We first try getComputedStyle.borderLeftWidth/borderRightWidth (accurate,
            // works even when widget is hidden — unlike getBoundingClientRect which returns
            // 0 for hidden elements).  However some landscape CSS themes have NO left/right
            // border on the collapsed card header (border is only on top/bottom).  In that
            // case getComputedStyle returns 0 and we fall back to the DPR formula which
            // accounts for sub-pixel border rendering and was the original working approach.
            const dpr = window.devicePixelRatio || 1;
            const _sampleCard = cardElements[0];
            const _sampleHeader = _sampleCard?.querySelector('.card-header') ?? _sampleCard?.firstElementChild ?? null;

            let _cardHeaderBorderLeft = 0, _cardHeaderBorderRight = 0;
            if (_sampleHeader) {
                const _hs = window.getComputedStyle(_sampleHeader);
                _cardHeaderBorderLeft = parseFloat(_hs.borderLeftWidth) || 0;
                _cardHeaderBorderRight = parseFloat(_hs.borderRightWidth) || 0;
            }
            // If CSS has no left/right border on the header (returns 0), fall back to the
            // DPR formula which was the original calculation and gives correct results.
            if (_cardHeaderBorderLeft === 0 && _cardHeaderBorderRight === 0) {
                // getComputedStyle.borderXWidth always reflects the CSS value (1px), not the
                // sub-pixel-rounded rendered value — Math.round(DPR)/DPR approximates the
                // rendered width, but at sub-integer DPR (e.g. 33% zoom → DPR≈0.33),
                // Math.round(0.33)=0 makes this evaluate to 0, zeroing out the border
                // entirely.  That underestimates actualCollapsedCardDomPx/openCardFullWidthPx,
                // letting the fit-check believe more (and larger) cards fit than actually do —
                // causing open tickets to overflow/crop at low zoom.  Clamp to a 1px minimum,
                // matching the fix applied to the portrait estimator (see WidgetCards.js).
                const _renderedBorderPx = Math.max(1, Math.round(dpr) / dpr); // e.g. 1.49px at DPR=0.67
                _cardHeaderBorderLeft = _renderedBorderPx;
                _cardHeaderBorderRight = _renderedBorderPx;
            }
            // headerBorderExtraPx: left + right border contribution to horizontal card width.
            const headerBorderExtraPx = _cardHeaderBorderLeft + _cardHeaderBorderRight;
            const bodyPaddingExtraPx = compactStatusStyle.bodyPaddingPx * 2;
            // actualCollapsedCardDomPx: rendered width of a collapsed card in CSS px.
            const actualCollapsedCardDomPx = collapsedHeaderMinWidthPxForCalc + headerBorderExtraPx;
            const cardOuterBorderPx = headerBorderExtraPx;
            const actualOpenBodyDomPx = primaryOpenBodyPx + bodyPaddingExtraPx;

            const _scenario = skipAnimation ? "RESIZE" : hasInitiallyLoadedRef.current ? "DATA-CHANGE" : "INITIAL-LOAD";
            const _diagDpr = window.devicePixelRatio || 1;
            const _diagBorder = Math.round(_diagDpr) / _diagDpr;
            const _dbg = String(normalizedCardsData[0]?.title || normalizedCardsData[0]?.id || "?").slice(0, 20);


            // ── Priority-first layout: Open → Medium → Collapsed ──────────────────
            // Open tickets are allocated FIRST from the FULL available width (no
            // collapsed baseline is pre-deducted before open cards are evaluated).
            // Medium (orphan) headers use whatever remains after open cards.
            // Collapsed stack fills the rest.
            const compressedHeaderWidth = actualCollapsedCardDomPx;
            const cardGapPx = isDarkTheme ? 5 : 3;
            // Same border-cancellation rationale as openCardFullWidthPx: border is in actualCollapsedCardDomPx
            const mediumUpgradePerCardPx = Math.max(0, mediumHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc) + cardGapPx;
            const fullOpenFallbackPx = dynamicOpenCardWidth;

            // Use displayCardsData.length (not cardElements.length) for the "bottom"
            // end index so open cards always land at the true end of the rendered list,
            const buildOpenIndexes = (count) => (count > 0
                ? (isOpenTicketPlacementTop
                    ? Array.from({ length: count }, (_, idx) => idx)
                    : Array.from({ length: count }, (_, idx) => cardElements.length - count + idx))
                : []);

            // ── Step 1: Open tickets (highest priority) ────────────────────────────
            // Full pixel cost per open card = header + body.
            // The fit-check includes the collapsed-baseline of all non-open cards so that
            // open + remaining collapsed together never exceed availableWidth (no overflow).
            // Border is already included in actualCollapsedCardDomPx — do NOT add cardOuterBorderPx
            // again here; it would double-count the left+right border (2.4px at DPR=0.833),
            // causing each open card to appear ~2.4px wider than it actually renders.
            const openCardFullWidthPx = actualCollapsedCardDomPx
                + (normalHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc)
                + actualOpenBodyDomPx;
            // Condition 0 (no-max excess-cards trim) removed — estimator now returns totalTickets,
            // so cardElements.length can never exceed estimatedTicketsToRender.

            // Gap counting for open cards depends on placement:
            // • RIGHT (bottom): the last open card IS the last array element → gap=0 for it → (N-1) gaps
            // • LEFT (top):  the last open card is NOT last (collapsed/medium follow) → gap=5 for all → N gaps
            // Using (N-1) for LEFT placement underestimates total width by exactly 1*cardGapPx,
            // causing applyCompressedModes to over-promote medium cards and produce overflow.
            const openGapCount = (n) => isOpenTicketPlacementTop ? n : Math.max(0, n - 1);

            const targetOpenCount = noOpenMode
                ? 0
                : (noMaxMode ? cardElements.length : Math.min(safeMinOpenCount, cardElements.length));
            let fittedOpenCount = targetOpenCount;
            while (fittedOpenCount > 0) {
                const nonOpenCount = cardElements.length - fittedOpenCount;
                // Collapsed non-open cards have 0 marginRight — do NOT add totalSpacing.
                const nonOpenBaselineWidth = nonOpenCount * actualCollapsedCardDomPx;
                const totalOpenWidth = fittedOpenCount * openCardFullWidthPx
                    + openGapCount(fittedOpenCount) * cardGapPx;
                if (totalOpenWidth + nonOpenBaselineWidth <= availableWidth) break;
                fittedOpenCount--;
            }



            const openIndexes = buildOpenIndexes(fittedOpenCount);
            const openIndexSet = new Set(openIndexes);

            openIndexes.forEach((idx) => {
                newCardStates[idx].isExpanded = true;
                newCardStates[idx].isHalfOpen = false;
                newCardStates[idx].visibleBodyHeight = Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx));
                newCardStates[idx].renderMode = "open";
            });

            const totalOpenConsumedPx = fittedOpenCount > 0
                ? fittedOpenCount * openCardFullWidthPx + openGapCount(fittedOpenCount) * cardGapPx
                : 0;
            let consumedOpenBudgetPx = totalOpenConsumedPx;

            // ── Steps 2 & 3: Medium then Collapsed ─────────────────────────────────
            const remainingIndexes = [];
            for (let idx = 0; idx < cardElements.length; idx++) {
                if (!openIndexSet.has(idx)) remainingIndexes.push(idx);
            }

            // Collapsed baseline for non-open cards only (each takes its minimum width).
            // Collapsed cards have 0 marginRight — do NOT add totalSpacing here.
            // totalSpacing is from the PREVIOUS render's open/medium card gaps which are
            // already counted in totalOpenConsumedPx. Including it would overflow the container.
            const nonOpenCollapsedBaseline = remainingIndexes.length * compressedHeaderWidth;
            // Budget for medium upgrades = remaining width after open cards and non-open baseline.
            let availableBodyWidth = Math.max(0, availableWidth - totalOpenConsumedPx - nonOpenCollapsedBaseline);

            // ── Baseline overflow / open-card-squeeze detection (landscape) ─────
            {
                const _cardCollapsedBaselineWidth = cardElements.length * actualCollapsedCardDomPx;
                const _baselineOverflows = _cardCollapsedBaselineWidth > availableWidth;
                const _noRoomForOpenCards = (availableWidth - _cardCollapsedBaselineWidth) < openCardFullWidthPx;
                if (_baselineOverflows || _noRoomForOpenCards) {
                    const reason = _baselineOverflows ? "baseline-overflow" : "open-card-squeeze";

                    overflowScroll = true;
                }
            }



            const applyCompressedModes = (indexes, remainingBodyHeightPx) => {
                const allocationOrder = isOpenTicketPlacementTop
                    ? indexes
                    : [...indexes].reverse();
                const nonOpenCount = indexes.length;
                // Reserve actualCollapsedCardDomPx as a safety margin so the last card
                // never visually touches the container right edge. Without this reserve,
                // medium promotions can fill the space to within 1-2px (sub-pixel rounding
                // then causes the last card to appear cropped against the border).
                const safetyMarginPx = actualCollapsedCardDomPx;
                const dynamicSpaceForCompressedModes = Math.max(0, remainingBodyHeightPx - safetyMarginPx);
                // Cost of promoting one card from collapsed to medium: header delta + marginRight gain.
                // Must match mediumUpgradePerCardPx so the reserve and the charge agree.
                const mediumUpgradePx = Math.max(1, mediumHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc) + cardGapPx;
                const maxMediumByBudget = Math.min(nonOpenCount, Math.floor(dynamicSpaceForCompressedModes / mediumUpgradePx));

                // Guarantee MIN_MEDIUM_HEADERS orphan headers only when remaining
                // space can actually afford them.  If not (e.g. open cards consumed
                // most of the viewport) fall back to budget-based count to avoid overflow.
                const canAffordGuarantee = dynamicSpaceForCompressedModes >= (MIN_MEDIUM_HEADERS * mediumUpgradePx);
                const guaranteedMedium = canAffordGuarantee ? Math.min(nonOpenCount, MIN_MEDIUM_HEADERS) : 0;
                const mediumCount = Math.max(guaranteedMedium, maxMediumByBudget);
                const consumedBudgetPx = mediumCount * mediumUpgradePx;

                //let findFirstLastMedium = false;

                allocationOrder.forEach((idx, orderIdx) => {
                    const shouldBeMedium = orderIdx < mediumCount;

                    // newCardStates[idx].lastCollapsedMode = false;
                    // if (!shouldBeMedium && !findFirstLastMedium) {
                    //     findFirstLastMedium = true;
                    //     console.log("First medium card found at index", idx);
                    //     newCardStates[idx].lastCollapsedMode = true;
                    // }

                    newCardStates[idx].isExpanded = false;
                    newCardStates[idx].isHalfOpen = false;
                    newCardStates[idx].visibleBodyHeight = 0;
                    newCardStates[idx].renderMode = shouldBeMedium ? "medium" : "collapsed";
                    newCardStates[idx].priorCompressedMode = shouldBeMedium ? "medium" : "collapsed";
                });

                return Math.max(0, dynamicSpaceForCompressedModes - consumedBudgetPx);
            };

            applyCompressedModes(remainingIndexes, availableBodyWidth);

            // ── Fix 3: Remaining-space distribution (horizontal) ──────────────────
            // After applyCompressedModes settles all card modes, compute how much
            // horizontal space is genuinely unused (real gaps vs. estimated) and:
            //   Step 1 — Promote collapsed → medium (closest to open first).
            //   Step 2 — Pull in more cards from normalizedCardsData if safe.
            if (openIndexes.length > 0) {
                // Recompute actual used width with real gap values.
                let actualUsedPx = 0;
                newCardStates.forEach((cs, idx) => {
                    const isLast = idx === newCardStates.length - 1;
                    // Collapsed cards have no marginRight gap.
                    const gap = (isLast || cs.renderMode === "collapsed") ? 0 : cardGapPx;
                    if (cs.renderMode === "open") {
                        actualUsedPx += actualCollapsedCardDomPx
                            + (normalHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc)
                            + actualOpenBodyDomPx + gap;
                    } else if (cs.renderMode === "medium") {
                        actualUsedPx += actualCollapsedCardDomPx
                            + (mediumHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc)
                            + gap;
                    } else {
                        actualUsedPx += actualCollapsedCardDomPx; // no gap for collapsed
                    }
                });
                let remainingPx = availableWidth - actualUsedPx;
                const remainingPxBeforeStep1 = remainingPx;

                // ── Step 1: Promote collapsed → medium ────────────────────────────
                let promotionCount = 0;
                if (remainingPx >= 1) {
                    const collapsedIdxs = [];
                    newCardStates.forEach((cs, i) => {
                        if (cs.renderMode === "collapsed") collapsedIdxs.push(i);
                    });
                    // Closest-to-open first (mirrors applyCompressedModes allocation order).
                    const promoOrder = isOpenTicketPlacementTop
                        ? collapsedIdxs
                        : [...collapsedIdxs].reverse();

                    for (const idx of promoOrder) {
                        if (remainingPx < 1) break;
                        const isLast = idx === newCardStates.length - 1;
                        // Cost: header delta + outer border + gap (0 if last card).
                        // Cost = actual width delta: collapsed→medium header upgrade + gap.
                        // cardOuterBorderPx is already in actualCollapsedCardDomPx — do NOT add it again.
                        const cost = (mediumHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc)
                            + (isLast ? 0 : cardGapPx);
                        if (remainingPx >= cost) {
                            newCardStates[idx].renderMode = "medium";
                            newCardStates[idx].priorCompressedMode = "medium";
                            remainingPx -= cost;
                            promotionCount++;
                        } else {
                            break; // keep the medium cluster contiguous
                        }
                    }
                }

                // ── Step 2: Pull in more cards if horizontal budget allows ────────
                // Uses the same formula as estimatedTicketsToRender to guarantee
                // the open-card fit-check still passes after the addition.
                const moreCardsAvailable = normalizedCardsData.length - displayCardsData.length;
                if (moreCardsAvailable > 0 && remainingPxBeforeStep1 >= actualCollapsedCardDomPx) {
                    const futureMediumReserve = Math.min(
                        (cardElements.length + moreCardsAvailable - openIndexes.length),
                        MIN_MEDIUM_HEADERS
                    ) * mediumUpgradePerCardPx;
                    const safeMaxTotal = Math.max(
                        openIndexes.length,
                        Math.floor(
                            Math.max(0, availableWidth - consumedOpenBudgetPx - futureMediumReserve)
                            / actualCollapsedCardDomPx
                        )
                    );
                    const safeByBudget = Math.max(0, safeMaxTotal - cardElements.length);
                    const safeBySpace = Math.floor(remainingPxBeforeStep1 / actualCollapsedCardDomPx);
                    // In no-max mode, cap Step 2 additions to estimatedTicketsToRender to
                    // prevent an oscillation loop with Condition 0 no-max-trim.
                    const safeByEstimate = noMaxMode
                        ? Math.max(0, estimatedTicketsToRender - cardElements.length)
                        : moreCardsAvailable;
                    const canAdd = Math.min(moreCardsAvailable, safeByBudget, safeBySpace, safeByEstimate);

                    if (canAdd > 0) {
                        if (!hasInitiallyLoadedRef.current) {
                            isMeasuringRef.current = false;
                            isCalculatingRef.current = false;
                            setDisableBodyTransition(false);
                            setMaxTicketsToRender(prev =>
                                Math.min(normalizedCardsData.length, prev + canAdd)
                            );
                            return;
                        } else {
                            isMeasuringRef.current = false;
                            isCalculatingRef.current = false;
                            noAnimationCalcRef.current = true;
                            isFirstCalculationRef.current = true;
                            step2ExpansionRef.current = true;
                            setMaxTicketsToRender(prev =>
                                Math.min(normalizedCardsData.length, prev + canAdd)
                            );
                            return;
                        }
                    }
                }
            }
            // ── Result summary ─────────────────────────────────────────────────────
            {
                const _openN = newCardStates.filter(s => s.renderMode === "open").length;
                const _medN = newCardStates.filter(s => s.renderMode === "medium").length;
                const _colN = newCardStates.filter(s => s.renderMode === "collapsed").length;
                // Compute exact rendered width for overflow detection
                let _renderedW = 0;
                newCardStates.forEach((cs, idx) => {
                    const isLast = idx === newCardStates.length - 1;
                    const gap = (isLast || cs.renderMode === "collapsed") ? 0 : cardGapPx;
                    if (cs.renderMode === "open") {
                        _renderedW += actualCollapsedCardDomPx + (normalHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc) + actualOpenBodyDomPx + gap;
                    } else if (cs.renderMode === "medium") {
                        _renderedW += actualCollapsedCardDomPx + (mediumHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc) + gap;
                    } else {
                        _renderedW += actualCollapsedCardDomPx;
                    }
                });
                const _overflow = _renderedW > availableWidth;

            }

            // Mark calculation as complete BEFORE setting states
            isCalculatingRef.current = false;
            // (disableBodyTransition is already set to false after measurement phase)

            if (currentCalcRunId !== calcRunIdRef.current) return;

            layoutMeasureTokenRef.current += 1;

            // Store the initial calculated state for auto-revert with data version
            if (!initialStateRef.current) {
                initialStateRef.current = {
                    states: JSON.parse(JSON.stringify(newCardStates)),  // Deep copy
                    dataVersion: dataVersionRef.current,
                    // Overflowing widgets need scroll in their initial layout too —
                    // auto-revert restores this instead of force-disabling scroll.
                    scrollEnabled: overflowScroll,
                };
            }

            // ── Entry animation: 3-stage sequence ──────────────────────────────
            // Stage 1: cards appear suddenly (no fade/slide), one by one left→right
            // Stage 2: headers expand one by one left→right
            // Stage 3: bodies expand one by one left→right
            const stage1States = newCardStates.map(s => ({
                ...s,
                isExpanded: false,
                visibleBodyHeight: 0,
                renderMode: "collapsed",
                lastCollapsedMode: false,
            }));

            hasInitiallyLoadedRef.current = true;
            // Mark that this widget has completed its one-time entry load — after this, any
            // re-init (zoom re-estimate, resize) stays silent and never shrinks the count.
            hasEverLoadedRef.current = true;
            calcRetryRef.current = 0;

            // ── Overflow handling ─────────────────────────────────────────────────
            // Rule: if the FULL ticket count fits in the container → expand to all
            // tickets and play the entry animation.  Only when the full count truly
            // does not fit → load everything silently with scroll enabled.
            {
                const _overflowRemaining = normalizedCardsData.length - displayCardsData.length;
                if (_overflowRemaining > 0) {
                    isMeasuringRef.current = false;
                    isCalculatingRef.current = false;
                    isFirstCalculationRef.current = true; // bypass the calc throttle
                    // Snapshot was captured with the partial card count — clear it so
                    // the full-count calc re-captures, keeping auto-revert working.
                    initialStateRef.current = null;

                    if (!skipAnimation) {
                        // Initial load: expand to the FULL ticket count and replay the
                        // initial calc via the full-reset path.  The replay measures
                        // all tickets in the DOM (authoritative) and decides:
                        //   fits → entry animation plays once with every ticket;
                        //   overflows → overflowScroll=true routes to the silent path.

                        hasInitiallyLoadedRef.current = false;
                        deferReplayRef.current = true; // wait for parallel entry animations
                        // Defer the full-count re-render too — committing hundreds of
                        // card nodes mid-animation of parallel widgets causes stutter.
                        _whenNoEntryAnimations(() => {
                            if (currentCalcRunId !== calcRunIdRef.current) return;
                            setMaxTicketsToRender(normalizedCardsData.length);
                        });
                        return;
                    }

                    // Silent recalc (resize/zoom): expand silently, keep scroll if overflowing.

                    noAnimationCalcRef.current = true;
                    step2ExpansionRef.current = true;
                    if (overflowScroll) setScrollEnabled(true);
                    setMaxTicketsToRender(normalizedCardsData.length);
                    return;
                }
            }

            // ── Silent recalculation path (no animation; also used for overflow) ──
            if (skipAnimation || overflowScroll) {
                hasUserInteractedRef.current = false;
                prevExpandedRef.current = new Set();
                // Preserve scroll when cards overflow the viewport; otherwise reset.
                setScrollEnabled(overflowScroll);
                entryAnimTimersRef.current.forEach(clearTimeout);
                entryAnimTimersRef.current = [];
                isEntryAnimatingRef.current = false;
                _entryAnimEnded(entryAnimCountedRef);
                setEntryAnimStage(0);
                // disableBodyTransition is already true from the top of this function.
                // Apply the new layout states immediately (no transition), then
                // re-enable transitions in the next frame so subsequent user
                // interactions animate correctly.
                hasAppliedTrailingSpaceRef.current = false;
                layoutMeasureTokenRef.current += 1;
                setCardStates(newCardStates);
                setIsReady(true);
                requestAnimationFrame(() => {
                    if (currentCalcRunId !== calcRunIdRef.current) return;
                    setDisableBodyTransition(false);
                    setDisableHeaderTransition(false);
                });
                return;
            }

            // When cards overflow the viewport, enable scroll immediately.
            if (overflowScroll) setScrollEnabled(true);

            // ── Entry animation path ─────────────────────────────────────────────
            // Set the animation flag BEFORE setCardStates so any ResizeObserver /
            // useEffect callbacks that fire during the re-render see the correct value
            // and don't accidentally schedule a second silent recalculation.
            isEntryAnimatingRef.current = true;
            _entryAnimStarted(entryAnimCountedRef);
            setCardStates(stage1States);

            requestAnimationFrame(() => {
                if (currentCalcRunId !== calcRunIdRef.current) return;
                // isEntryAnimatingRef.current is already true (set synchronously above).

                const N = displayCardsData.length;
                const STAGGER_CARD = 60;
                const STAGGER_HEADER = 250;  // matches entry header transition (220ms) + buffer
                const STAGGER_BODY = 950;    // matches body transition (900ms) + buffer
                const HEADER_TRANSITION_MS = 220; // entry transition duration (must match CSS above)
                const BODY_TRANSITION_MS = 900;   // same as interactive body transition

                const addTimer = (fn, delay) => {
                    const id = setTimeout(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        fn();
                    }, delay);
                    entryAnimTimersRef.current.push(id);
                };

                setEntryAnimStage(1);
                setIsReady(true);

                const startStage3 = () => {
                    setDisableBodyTransition(false);
                    setEntryAnimStage(3);
                    const revealBody = (i) => {
                        if (i >= N) {
                            addTimer(() => {
                                setEntryAnimStage(0);
                                isEntryAnimatingRef.current = false;
                                _entryAnimEnded(entryAnimCountedRef);
                                layoutMeasureTokenRef.current += 1;
                                setCardStates(prev => prev.map(s => ({ ...s })));
                            }, BODY_TRANSITION_MS + 200);
                            return;
                        }
                        if (newCardStates[i].isExpanded) {
                            setCardStates(prev => {
                                const next = prev.map(s => ({ ...s }));
                                next[i] = {
                                    ...next[i],
                                    isExpanded: true,
                                    renderMode: "open",
                                    visibleBodyHeight: newCardStates[i].visibleBodyHeight,
                                };
                                return next;
                            });
                            addTimer(() => revealBody(i + 1), STAGGER_BODY);
                        } else {
                            revealBody(i + 1);
                        }
                    };
                    requestAnimationFrame(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        revealBody(0);
                    });
                };

                const startStage2 = () => {
                    setDisableHeaderTransition(false);
                    setEntryAnimStage(2);
                    const revealHeader = (i) => {
                        if (i >= N) {
                            addTimer(startStage3, HEADER_TRANSITION_MS + 80);
                            return;
                        }
                        const state = newCardStates[i];
                        const targetMode = state.renderMode === "open" ? "medium" : state.renderMode;
                        if (targetMode !== "collapsed") {
                            setCardStates(prev => {
                                const next = prev.map(s => ({ ...s }));
                                next[i] = {
                                    ...next[i],
                                    renderMode: targetMode,
                                    priorCompressedMode: state.priorCompressedMode,
                                    lastCollapsedMode: state.lastCollapsedMode,
                                };
                                return next;
                            });
                            addTimer(() => revealHeader(i + 1), STAGGER_HEADER);
                        } else {
                            revealHeader(i + 1);
                        }
                    };
                    requestAnimationFrame(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        revealHeader(0);
                    });
                };

                const lastCardTime = Math.min(N - 1, 8) * STAGGER_CARD;
                // Per-widget phase offset — de-synchronizes parallel widgets' stage-2/3
                // timer grids so their setCardStates re-renders interleave across frames.
                addTimer(startStage2, lastCardTime + 80 + _getWidgetAnimStagger());
            });
        });
    };

    const toggleCard = (index) => {
        hasUserInteractedRef.current = true;
        setScrollEnabled(true);
        setDisableBodyTransition(false);
        setDisableHeaderTransition(false);
        if (revertAnimationTimerRef.current) {
            clearTimeout(revertAnimationTimerRef.current);
            revertAnimationTimerRef.current = null;
        }
        isRevertAnimatingRef.current = false;
        layoutMeasureTokenRef.current += 1;

        setCardStates((prevStates) => {
            // Clone only the 1-3 cards that actually change so unchanged cards keep
            // referential identity and memoized card components skip re-rendering.
            const newStates = [...prevStates];
            const currentMode = newStates[index]?.renderMode;

            if (currentMode === "open") {
                // Open ticket header clicked → collapse to orphan header only
                newStates[index] = {
                    ...newStates[index],
                    isExpanded: false,
                    isHalfOpen: false,
                    visibleBodyHeight: 0,
                    renderMode: "medium",
                    priorCompressedMode: "medium",
                };
            } else if (currentMode === "medium") {
                // Orphan header clicked → open this ticket only.
                // Match the width of any already-open card so all open cards are the same size.
                const alreadyOpen = newStates.find((s, i) => i !== index && s.isExpanded);
                // Match an already-open peer's width; otherwise use the CURRENT
                // dynamicOpenCardWidth. Do NOT fall back to newStates[index].fullHeight —
                // after a resize that's a stale pre-zoom width.
                const consistentWidth = alreadyOpen
                    ? alreadyOpen.visibleBodyHeight
                    : Math.floor(dynamicOpenCardWidth);
                newStates[index] = {
                    ...newStates[index],
                    isExpanded: true,
                    isHalfOpen: false,
                    visibleBodyHeight: consistentWidth,
                    renderMode: "open",
                };
            } else {
                // Stack (collapsed) clicked → convert clicked + before + after to orphan headers.
                // Only pull adjacent cards in when they are STILL inside the stack
                // (renderMode "collapsed"). Cards already shown as open headers stay untouched.
                const toConvert = new Set([index]);
                if (index > 0 && newStates[index - 1]?.renderMode === "collapsed") toConvert.add(index - 1);
                if (index < newStates.length - 1 && newStates[index + 1]?.renderMode === "collapsed") toConvert.add(index + 1);

                toConvert.forEach((i) => {
                    newStates[i] = {
                        ...newStates[i],
                        isExpanded: false,
                        isHalfOpen: false,
                        visibleBodyHeight: 0,
                        renderMode: "medium",
                        priorCompressedMode: "medium",
                    };
                });
            }

            return newStates;
        });

        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
        }

        if (autoRevertDurationinSec > 0) {
            const currentVersion = dataVersionRef.current;
            autoRevertTimerRef.current = setTimeout(() => {
                if (initialStateRef.current &&
                    initialStateRef.current.dataVersion === currentVersion &&
                    initialStateRef.current.states.length === cardStates.length) {
                    // Refresh open cards to the CURRENT dynamicOpenCardWidth — the snapshot
                    // was captured at an earlier zoom (a silent unchanged-count resize never
                    // re-captures it), so reverting verbatim would reapply a stale width.
                    const _openW = Math.floor(dynamicOpenCardWidth);
                    const restoredState = JSON.parse(JSON.stringify(initialStateRef.current.states)).map((s) =>
                        (s.renderMode === "open" || s.isExpanded)
                            ? { ...s, fullHeight: _openW, visibleBodyHeight: _openW }
                            : s
                    );
                    // Reset interaction flags so measureAndApply can re-run after the
                    // revert animation settles. Allow transitions so the reset animates smoothly.
                    hasUserInteractedRef.current = false;
                    prevExpandedRef.current = new Set();
                    setScrollEnabled(Boolean(initialStateRef.current.scrollEnabled));
                    isRevertAnimatingRef.current = true;
                    setDisableBodyTransition(false);
                    setDisableHeaderTransition(false);
                    layoutMeasureTokenRef.current += 1;
                    setCardStates(restoredState);
                    revertAnimationTimerRef.current = setTimeout(() => {
                        isRevertAnimatingRef.current = false;
                        revertAnimationTimerRef.current = null;
                        setDisableBodyTransition(true);
                    }, 1100);
                }
                autoRevertTimerRef.current = null;
            }, autoRevertDurationinSec * 1000);
        }
    };

    const handleImageError = useCallback((cardId) => {
        setImageErrors((prev) => ({ ...prev, [cardId]: true }));
    }, []);

    // Stable wrapper so memoized cards don't re-render just because toggleCard
    // is a fresh closure each render.
    toggleCardLatestRef.current = toggleCard;

    const shouldShowCards = isReady || hasInitiallyLoadedRef.current;

    return (
        <div ref={parentRef} className={classes.landscapeRoot} style={{ height: "100%", width: "100%", backgroundColor: isDarkTheme ? "#1a1a1a" : "#f0f0f0" }}>
            <div
                ref={scrollContainerRef}
                className={`${isDarkTheme ? "slim-scroll-dark" : "slim-scroll"} ${classes.landscapeScroll}`}
                style={{
                    width: "100%",
                    height: "100%",
                    overflowX: scrollEnabled ? "auto" : "hidden",
                    overflowY: "hidden",
                    // Reserve the scrollbar gutter once scrolling is enabled — without it
                    // the horizontal scrollbar pops in/out as content width crosses the
                    // overflow threshold mid-animation, changing the container height and
                    // forcing a full relayout of every card (lag in large widgets only).
                    scrollbarGutter: scrollEnabled ? "stable" : "auto",
                    opacity: shouldShowCards ? 1 : 0,
                    visibility: shouldShowCards ? "visible" : "hidden",
                    transition: "none",
                    backgroundColor: isDarkTheme ? "#1a1a1a" : "#f0f0f0",
                }}
            >
                <div
                    className={classes.landscapeGrid}
                    ref={gridRef}
                    style={{
                        // paddingTop: "8px",
                        // paddingLeft: "8px",
                        // paddingRight: "8px",
                        // paddingBottom: "8px",
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        alignItems: "stretch",
                        alignContent: "flex-start",
                        gap: "0px",
                        height: "100%",
                        boxSizing: "border-box",
                        margin: "0 8px",
                    }}
                >
                    {displayCardsData.map((card, index) => (
                        <LandscapeTicketCard
                            key={card.id}
                            card={card}
                            index={index}
                            cardState={cardStates[index] || EMPTY_CARD_STATE}
                            isLastCard={index === displayCardsData.length - 1}
                            isDarkTheme={isDarkTheme}
                            themeStyles={themeStyles}
                            compactStatusStyle={compactStatusStyle}
                            entryAnimStage={entryAnimStage}
                            isReady={isReady}
                            disableBodyTransition={disableBodyTransition}
                            disableHeaderTransition={disableHeaderTransition}
                            isOpenTicketPlacementTop={isOpenTicketPlacementTop}
                            imageError={Boolean(imageErrors[card.id])}
                            onToggle={onToggleCard}
                            onImageError={handleImageError}
                            hasUserInteractedRef={hasUserInteractedRef}
                            isEntryAnimatingRef={isEntryAnimatingRef}
                            cardRefs={cardRefs}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
